/**
 * The desk's data: every watched site, the summary, the engine's state, and -
 * for somebody who may add sites - the clients whose website nobody watches yet.
 *
 * The same race and polling shape as the R9 watch card (`useSiteWatch.ts`): a
 * newer read always wins over an older one still in flight, a failed read is a
 * flag the page turns into a sentence rather than a throw, and while the engine
 * is on and the tab is visible the list is read again once a minute - the
 * engine's own tick - so an outage shows up on an open page without a reload.
 *
 * Search and the state chips filter in the browser. A desk holds a few dozen to
 * a few hundred sites, all of them already here, and a chip that waited for the
 * network would make the page feel slower than the list is long.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  listMonitorSuggestions,
  listMonitors,
  type MonitorListResponse,
  type MonitorRow,
  type MonitorSuggestion,
} from "../../../lib/monitoring";

/** The engine ticks once a minute; reading more often shows nothing new. */
export const MONITOR_POLL_MS = 60_000;

/** Runs `fn` every `ms` while `on` and the tab is visible, and once more the
 *  moment a hidden tab comes back - the page may have been away for an hour. */
export function useVisiblePoll(on: boolean, ms: number, fn: () => void) {
  const latest = useRef(fn);
  latest.current = fn;
  useEffect(() => {
    if (!on) return;
    const visible = () => typeof document === "undefined" || document.visibilityState === "visible";
    const timer = setInterval(() => {
      if (visible()) latest.current();
    }, ms);
    const onShow = () => {
      if (visible()) latest.current();
    };
    document.addEventListener("visibilitychange", onShow);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onShow);
    };
  }, [on, ms]);
}

export function useMonitors({ withSuggestions }: { withSuggestions: boolean }) {
  const [data, setData] = useState<MonitorListResponse | null>(null);
  const [failed, setFailed] = useState(false);
  const [suggestions, setSuggestions] = useState<MonitorSuggestion[]>([]);

  const requestId = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    const [list, hints] = await Promise.allSettled([
      listMonitors(),
      withSuggestions ? listMonitorSuggestions() : Promise.resolve({ suggestions: [] }),
    ]);
    // A newer read started while this one was out: its answer wins.
    if (!alive.current || id !== requestId.current) return;
    if (list.status === "fulfilled") {
      setData(list.value);
      setFailed(false);
    } else {
      setFailed(true);
    }
    // The suggestions are a convenience under the table. A failed read leaves
    // the last good list rather than a warning nobody needs.
    if (hints.status === "fulfilled") setSuggestions(hints.value.suggestions);
  }, [withSuggestions]);

  useEffect(() => {
    void load();
  }, [load]);

  useVisiblePoll(Boolean(data?.engine.enabled), MONITOR_POLL_MS, () => void load());

  /** Puts one row's fresh answer in place (after check now, pause, edit) without
   *  waiting for the next read. The summary catches up on that read. */
  const replaceRow = useCallback((site: MonitorRow) => {
    setData((d) => (d ? { ...d, sites: d.sites.map((s) => (s.id === site.id ? site : s)) } : d));
  }, []);

  return {
    data,
    loading: data === null && !failed,
    failed,
    suggestions,
    reload: load,
    replaceRow,
  };
}
